import { createSlice, createSelector } from "@reduxjs/toolkit";
import moment from "moment";
import { CnCOrderResponse, CnCTransactionResponse } from "../types/api";

export interface OrderState {
  receivedOrders: CnCOrderResponse[];
  processedOrders: CnCOrderResponse[];

  // Mixed types by design - supports both local state changes and backend data
  // - Local finish/reject: CnCOrderResponse with completed status
  // - Backend transactions: CnCTransactionResponse from API
  finishedOrders: (CnCOrderResponse | CnCTransactionResponse)[];

  newOrderFailed: boolean;
  loadingNewOrder: boolean;

  ordersFailed: boolean;
  loadingOrders: boolean;

  orderReceived: boolean;
  orderPending: boolean;

  finishOrderFailed: boolean;
  loadingFinishOrder: boolean;

  processOrderFailed: boolean;
  processingOrder: boolean;

  prepareOrderFailed: boolean;
  preparingOrder: boolean;

  articleAvailability: null;

  transactionsFailed: boolean;
  loadingTransactions: boolean;

  activateWhistle: boolean;

  thisOrder: CnCOrderResponse | null;

  newOrderNotification: boolean;

  dismissingSuspendedOrder: boolean;
}

const initialState: OrderState = {
  receivedOrders: [],
  processedOrders: [],
  finishedOrders: [],

  newOrderFailed: false,
  loadingNewOrder: false,

  ordersFailed: false,
  loadingOrders: false,

  orderReceived: true,
  orderPending: false,

  finishOrderFailed: false,
  loadingFinishOrder: false,

  processOrderFailed: false,
  processingOrder: false,

  prepareOrderFailed: false,
  preparingOrder: false,

  articleAvailability: null,

  transactionsFailed: false,
  loadingTransactions: false,

  activateWhistle: false,

  thisOrder: null,

  newOrderNotification: false,

  dismissingSuspendedOrder: false,
};

const orderSlice = createSlice({
  name: "orderSlice",
  initialState: initialState,
  reducers: {
    orderReceived: (state, action) => {
      state.orderReceived = action.payload;
    },
    orderPending: (state, action) => {
      state.orderPending = action.payload;
      state.orderReceived = false;
    },
    fetchNewOrderStart: (state) => {
      state.loadingNewOrder = true;
      state.newOrderFailed = false;
    },
    newOrderReceived: (state, action) => {
      state.receivedOrders = [...state.receivedOrders, action.payload];
      state.loadingNewOrder = false;
      state.newOrderFailed = false;
      state.activateWhistle = true;
      // state.playSound = true;
    },
    fetchNewOrderFailed: (state) => {
      state.loadingNewOrder = false;
      state.newOrderFailed = true;
    },
    fetchOrdersStart: (state) => {
      state.ordersFailed = false;
      state.loadingOrders = true;
    },
    fetchOrdersFailed: (state) => {
      state.ordersFailed = false;
      state.loadingOrders = true;
    },
    orders: (state, action) => {
      // order.status 'CREATED' 'PROCESSED'-u obradi 'PREPARED'-spakovano
      // TRANSAKCIJE 'CANCELED' 'REJECTED' 'TAKEN'
      state.receivedOrders = [];
      state.processedOrders = [];
      let sortedArray = [...action.payload];
      sortedArray.sort((a, b) =>
        moment(a.created).isBefore(moment(b.created)) ? 1 : -1
      );
      sortedArray.forEach((order: any) =>
        (order.status === "PROCESSED" || order.status === "PREPARED"
          ? state.processedOrders
          : state.receivedOrders
        ).push(order)
      );
      state.ordersFailed = false;
      state.loadingOrders = false;
    },
    driverOrders: (state, action) => {
      const userId = localStorage.getItem("id");

      state.receivedOrders = [];
      state.processedOrders = [];
      state.finishedOrders = [];

      action.payload.forEach((item: any) => {
        // Robust type detection based on backend reality
        const isTransaction =
          "orderNumber" in item && "userPhoneNumber" in item;

        if (isTransaction) {
          const transaction = item as CnCTransactionResponse;
          if (
            transaction.status === "TAKEN" &&
            transaction.userDeliveryResponse?.id.toString() === userId
          ) {
            state.finishedOrders.push(transaction);
          }
        } else {
          const order = item as CnCOrderResponse;
          if (
            order.userDeliveryResponse === null ||
            order.userDeliveryResponse === undefined
          ) {
            state.receivedOrders.push(order);
          } else {
            if (
              (order.status === "IN_DELIVERY" || order.status === "PREPARED") &&
              order.userDeliveryResponse.id.toString() === userId
            ) {
              state.processedOrders.push(order);
            }
          }
        }
      });

      state.ordersFailed = false;
      state.loadingOrders = false;
    },
    toggleOrderModal: (state, action) => {
      state.thisOrder = action.payload;
    },
    processOrder: (state, action) => {
      const processedOrder = state.receivedOrders.find(
        (order) => order.id === action.payload.orderId
      );
      if (processedOrder) {
        state.processedOrders.push({
          ...processedOrder,
          status: "PROCESSED",
          willBeReadyIn: action.payload.responseData.willBeReadyIn,
          willBeReadyInDate: action.payload.responseData.willBeReadyInDate,
          modified: action.payload.responseData.modified,
          noteForUser: action.payload.noteForUser,
        });

        state.receivedOrders = state.receivedOrders.filter(
          (order) => order.id !== action.payload.orderId
        );
      }

      state.processOrderFailed = false;
      state.processingOrder = false;
    },
    processOrderFailed: (state) => {
      state.processOrderFailed = true;
      state.processingOrder = false;
    },
    processOrderStart: (state) => {
      state.processOrderFailed = false;
      state.processingOrder = true;
    },
    preparedOrder: (state, action) => {
      state.processedOrders = state.processedOrders.map((order) => {
        if (order.id === action.payload) {
          return {
            ...order,
            status: "PREPARED",
          };
        } else {
          return order;
        }
      });
    },
    preparedOrderFailed: (state) => {
      state.prepareOrderFailed = true;
      state.preparingOrder = false;
    },
    preparedOrderStart: (state) => {
      state.prepareOrderFailed = false;
      state.preparingOrder = true;
    },
    finishOrderStart: (state) => {
      state.loadingFinishOrder = true;
      state.finishOrderFailed = false;
    },
    finishOrder: (state, action) => {
      const finishedOrder = state.processedOrders.find(
        (order) => order.id === action.payload.cncOrderId
      );
      if (finishedOrder) {
        // Add to finished orders as CnCOrderResponse with new status
        state.finishedOrders.push({
          ...finishedOrder,
          status: action.payload.status,
          ...(action.payload.status === "CANCELED" && {
            noteForCanceledStatus: action.payload.note,
          }),
        });

        // Remove from processed orders
        state.processedOrders = state.processedOrders.filter(
          (order) => order.id !== action.payload.cncOrderId
        );
      }

      state.loadingFinishOrder = false;
      state.finishOrderFailed = false;
    },
    rejectOrder: (state, action) => {
      const rejectedOrder = state.receivedOrders.find(
        (order) => order.id === action.payload.cncOrderId
      );
      if (rejectedOrder) {
        state.finishedOrders.push({
          ...rejectedOrder,
          status: "REJECTED",
          noteForUser: action.payload.note,
        });

        state.receivedOrders = state.receivedOrders.filter(
          (order) => order.id !== action.payload.cncOrderId
        );
      }

      state.loadingFinishOrder = false;
      state.finishOrderFailed = false;
    },
    finishOrderFailed: (state) => {
      state.loadingFinishOrder = false;
      state.finishOrderFailed = true;
    },
    fetchTransactionsStart: (state) => {
      state.transactionsFailed = false;
      state.loadingTransactions = true;
    },
    transactions: (state, action) => {
      const newTransactions: CnCTransactionResponse[] = action.payload;

      // Apply filtering and sorting to incoming transactions
      const cutoff = moment().subtract(72, "hours");
      const processedTransactions = newTransactions
        .filter((transaction: CnCTransactionResponse) =>
          moment(transaction.created).isAfter(cutoff)
        )
        .sort((a: CnCTransactionResponse, b: CnCTransactionResponse) =>
          moment(a.created).isBefore(moment(b.created)) ? 1 : -1
        );

      // Create set for efficient lookup of transaction order numbers
      const transactionOrderNumbers = new Set(
        processedTransactions
          .map((t) => t.orderNumber)
          .filter((orderNumber) => orderNumber != null) // Handle potential undefined/null values
      );

      // FIXED LOGIC: Rebuild finishedOrders properly
      state.finishedOrders = state.finishedOrders.filter((item) => {
        const isTransaction =
          "orderNumber" in item && "userPhoneNumber" in item;

        if (isTransaction) {
          // Remove ALL existing transactions - they'll be replaced with fresh data
          return false;
        }

        // Keep local finished orders that haven't been converted to backend transactions yet
        return !transactionOrderNumbers.has(item.id);
      });

      // Add fresh transaction data (no duplicates possible now)
      state.finishedOrders.push(...processedTransactions);

      state.transactionsFailed = false;
      state.loadingTransactions = false;
    },
    fetchTransactionsFailed: (state) => {
      state.transactionsFailed = true;
      state.loadingTransactions = false;
    },
    clearOrdersAndTransactions: (state) => {
      state.receivedOrders = [];
      state.processedOrders = [];
      state.finishedOrders = [];
    },
    newOrderDesktopNotification: (state, action) => {
      state.newOrderNotification = action.payload;
    },
    dismissSuspendedOrderStart: (state) => {
      state.dismissingSuspendedOrder = true;
    },
    dismissSuspendedOrderSuccess: (state, action) => {
      // Update the suspendedInfoShown flag in finishedOrders
      const transactionId = action.payload;
      state.finishedOrders = state.finishedOrders.map((item) => {
        if ("orderNumber" in item && item.id === transactionId) {
          return { ...item, suspendedInfoShown: true };
        }
        return item;
      });
      state.dismissingSuspendedOrder = false;
    },
    dismissSuspendedOrderFailed: (state) => {
      state.dismissingSuspendedOrder = false;
    },
  },
  extraReducers: (builder) => {
    // ITAM-2622 (3.6): orders carry patient names, phones and addresses, so
    // this slice must not survive an auth boundary. A token refresh dispatches
    // `loginSuccess`, never `auth/logout`, which is why the persona-exit action
    // is listened for as well.
    builder.addCase("auth/logout", () => initialState);
    builder.addCase("auth/personaExit", () => initialState);
  },
});

export const orderActions = orderSlice.actions;

// Selector for unshown suspended orders
export const selectUnshownSuspendedOrders = createSelector(
  [(state: { order: OrderState }) => state.order.finishedOrders],
  (finishedOrders) => {
    return finishedOrders.filter((item): item is CnCTransactionResponse => {
      // Must be a transaction (has orderNumber)
      const isTransaction = "orderNumber" in item && "userPhoneNumber" in item;
      if (!isTransaction) return false;

      const transaction = item as CnCTransactionResponse;
      return (
        transaction.status === "SUSPENDED" &&
        transaction.suspendedInfoShown === false
      );
    });
  }
);

export default orderSlice;
